import React, { useState } from 'react';
import styled from 'styled-components';

/**
 * The three ways a mini PC (or an OrangePi) with ONE usable port gets wired for
 * sub-vendos — as tabs, each with a picture, in the words of the people who
 * wire them.
 *
 * Why this exists: testers refuse USB-to-LAN adapters ("a throw-away part"),
 * and one of them chained Huawei 5V5 fiber modems and asked why "VLAN 12 didn't
 * come out". The answer is always the same three pictures — one device between
 * the mini PC and the access points has to turn a VLAN tag into a port — so the
 * pictures live here: in the register wizard's guide, on its Done step, and on
 * the Sub-vendo wiring card. Setup C was proven on the bench 2026-09-23; Setup B
 * was redrawn 2026-10-05 from the bench: the 5V5 preset of the public pisowifi
 * tutorial (LAN4 ISP, LAN3/LAN2 AP, LAN1 to the OrangePi/mini PC), with a second
 * modem on the first one's freed LAN2. The first drawing (internet on LAN1, mini
 * PC on LAN2) was never benched and left a mini PC offline on that preset; it
 * was removed (Ray, 2026-10-05).
 */
type Setup = {
   key: 'managed' | 'modem' | 'plain';
   tab: string;
   title: string;
   steps: string[];
   Diagram: React.FC;
};

// --- diagram pieces -----------------------------------------------------------
const box = (x: number, y: number, w: number, h: number, fill: string, stroke: string, label: string, sub?: string) => (
   <g>
      <rect x={x} y={y} width={w} height={h} rx={8} fill={fill} stroke={stroke} strokeWidth={1.5} />
      <text x={x + w / 2} y={y + (sub ? h / 2 - 4 : h / 2 + 4)} textAnchor="middle" fontSize={12} fontWeight={600} fill="#111827">
         {label}
      </text>
      {sub && (
         <text x={x + w / 2} y={y + h / 2 + 12} textAnchor="middle" fontSize={10} fill="#4b5563">
            {sub}
         </text>
      )}
   </g>
);
const line = (x1: number, y1: number, x2: number, y2: number, color = '#6b7280', dashed = false) => (
   <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={2} strokeDasharray={dashed ? '6 4' : undefined} />
);
const tag = (x: number, y: number, text: string, color = '#1d4ed8') => (
   <g>
      <rect x={x - 4} y={y - 11} width={text.length * 6.2 + 8} height={16} rx={4} fill="#fff" stroke={color} strokeWidth={1} />
      <text x={x} y={y + 1} fontSize={10} fill={color} fontWeight={600}>
         {text}
      </text>
   </g>
);
const INTERNET = ['#f3f4f6', '#9ca3af'] as const;
const MINIPC = ['#dbeafe', '#2563eb'] as const;
const SWITCH = ['#dcfce7', '#16a34a'] as const;
const AP = ['#ffedd5', '#ea580c'] as const;

// Grid (640 x 230): sources on the left (x 20-140), the middle device at
// x 260-380, targets on the right (x 500-620). Labels sit on the open middle
// of each line (x 150-250 and 390-490), never over a box.
const ManagedDiagram: React.FC = () => (
   <Svg viewBox="0 0 640 230" role="img" aria-label="Managed switch wiring">
      {box(20, 20, 120, 46, INTERNET[0], INTERNET[1], 'Internet', 'modem / fiber line')}
      {box(20, 150, 120, 46, MINIPC[0], MINIPC[1], 'Mini PC', 'its one port')}
      {box(260, 85, 120, 50, SWITCH[0], SWITCH[1], 'Managed switch')}
      {box(500, 20, 120, 46, AP[0], AP[1], 'Access point', 'sub-vendo 1')}
      {box(500, 150, 120, 46, AP[0], AP[1], 'Access point', 'sub-vendo 2')}
      {line(140, 43, 260, 100)}
      {tag(160, 58, 'plain port', '#4b5563')}
      {line(140, 173, 260, 120, MINIPC[1])}
      {tag(150, 160, 'trunk: internet + VLANs tagged')}
      {line(380, 100, 500, 43, AP[1])}
      {tag(400, 58, 'port on VLAN 11', AP[1])}
      {line(380, 120, 500, 173, AP[1])}
      {tag(400, 160, 'port on VLAN 12', AP[1])}
   </Svg>
);

// One 5V5 with its four ports IN THE ORDER PRINTED ON THE MODEM (LAN4 on the
// left, LAN1 on the right): counting them the other way round cost a bench
// round on 2026-10-05. `chipsY` places the port row, so a second modem can
// carry its ports on the edge facing the first one.
const modemPorts = (x: number, chipsY: number, strokes: Record<number, string>, dashed: number[] = []) =>
   [4, 3, 2, 1].map((port, i) => {
      const cx = x + 35 + i * 50;
      return (
         <g key={port}>
            <rect
               x={cx - 17}
               y={chipsY}
               width={34}
               height={22}
               rx={4}
               fill="#fff"
               stroke={strokes[port]}
               strokeWidth={1.5}
               strokeDasharray={dashed.includes(port) ? '4 3' : undefined}
            />
            <text x={cx} y={chipsY + 15} textAnchor="middle" fontSize={10} fontWeight={600} fill="#111827">
               LAN{port}
            </text>
         </g>
      );
   });

// The public pisowifi preset (LAN4 ISP, LAN3/LAN2 AP, LAN1 to the OrangePi or
// mini PC), as measured on the bench 2026-10-05 with a VLAN 22 and a VLAN 33
// 5V5. The internet and the mini PC STAY on the first modem; a second modem
// goes on its LAN2 once that port is freed (two ticks in the modem). Without
// that change the same modems work as a chain - the last caption line - but
// then the internet line moves every time a modem is added.
const ModemDiagram: React.FC = () => (
   <Svg viewBox="0 0 640 264" role="img" aria-label="Fiber modem as the switch wiring">
      <text x={320} y={20} textAnchor="middle" fontSize={11} fontWeight={600} fill="#111827">
         Internet into LAN4 · Mini PC into LAN1 · both stay on the first modem
      </text>
      {line(140, 93, 228, 93)}
      {line(412, 93, 500, 93, MINIPC[1])}
      {line(345, 104, 395, 166, '#6b7280', true)}
      {box(40, 68, 100, 50, INTERNET[0], INTERNET[1], 'Internet', 'fiber / ISP')}
      {box(500, 68, 100, 50, MINIPC[0], MINIPC[1], 'Mini PC', 'its one port')}
      <rect x={190} y={36} width={260} height={76} rx={10} fill="#f3f4f6" stroke="#9ca3af" strokeWidth={1.5} />
      <text x={320} y={54} textAnchor="middle" fontSize={12} fontWeight={600} fill="#111827">
         Modem 1 · Huawei 5V5
      </text>
      <text x={320} y={69} textAnchor="middle" fontSize={10} fill="#4b5563">
         WiFi + LAN3 (extra AP) = sub-vendo on VLAN 22
      </text>
      {modemPorts(210, 82, { 4: '#6b7280', 3: AP[1], 2: '#6b7280', 1: MINIPC[1] }, [2])}
      <rect
         x={190}
         y={160}
         width={260}
         height={76}
         rx={10}
         fill="#f3f4f6"
         stroke="#9ca3af"
         strokeWidth={1.5}
         strokeDasharray="6 4"
      />
      {modemPorts(210, 166, { 4: '#6b7280', 3: AP[1], 2: AP[1], 1: MINIPC[1] })}
      <text x={320} y={208} textAnchor="middle" fontSize={12} fontWeight={600} fill="#111827">
         Modem 2 — only if needed
      </text>
      <text x={320} y={223} textAnchor="middle" fontSize={10} fill="#4b5563">
         WiFi + LAN2/LAN3 (extra APs) = sub-vendo VLAN 33
      </text>
      <text x={458} y={134} fontSize={10} fontWeight={600} fill="#111827">
         Free LAN2 on Modem 1 first:
      </text>
      <text x={458} y={148} fontSize={10} fill="#4b5563">
         untick it in WAN › Binding Options
      </text>
      <text x={458} y={162} fontSize={10} fill="#4b5563">
         and in LAN › Layer 2/3 Port
      </text>
      <text x={320} y={254} textAnchor="middle" fontSize={10} fill="#4b5563">
         No settings change? Chain them: Modem 2’s LAN1 into Modem 1’s LAN4, internet into Modem 2’s LAN4.
      </text>
   </Svg>
);

const PlainDiagram: React.FC = () => (
   <Svg viewBox="0 0 640 230" role="img" aria-label="Plain switch with tag-aware access points">
      {box(20, 20, 120, 46, INTERNET[0], INTERNET[1], 'Internet', 'modem / fiber line')}
      {box(20, 150, 120, 46, MINIPC[0], MINIPC[1], 'Mini PC', 'its one port')}
      {box(260, 85, 120, 50, '#fef9c3', '#ca8a04', 'Plain switch', 'passes tags through')}
      {box(500, 20, 120, 46, AP[0], AP[1], 'Access point', 'tags VLAN 11 itself')}
      {box(500, 150, 120, 46, AP[0], AP[1], 'Access point', 'tags VLAN 12 itself')}
      {line(140, 43, 260, 100)}
      {line(140, 173, 260, 120, MINIPC[1])}
      {tag(150, 160, 'internet + VLANs tagged')}
      {line(380, 100, 500, 43, AP[1])}
      {line(380, 120, 500, 173, AP[1])}
      <text x={320} y={222} textAnchor="middle" fontSize={10} fill="#b91c1c" fontWeight={600}>
         Anything untagged on this switch gets raw internet — only tag-aware gear here
      </text>
   </Svg>
);

export const WIRING_SETUPS: readonly Setup[] = [
   {
      key: 'managed',
      tab: 'Setup A · Managed switch',
      title: 'Managed switch — the safe default',
      steps: [
         'Internet line, mini PC and every access point all plug into the switch.',
         'The port to the mini PC is a trunk: internet untagged, every sub-vendo VLAN tagged.',
         'The internet line goes on a plain port. Each sub-vendo access point goes on the port set to its VLAN.',
      ],
      Diagram: ManagedDiagram,
   },
   {
      key: 'modem',
      tab: 'Setup B · Fiber modem as the switch',
      title: 'Fiber modem as the switch (Huawei 5V5, internet on LAN4) — nothing to buy',
      steps: [
         'Internet line into LAN4 and the mini PC into LAN1 of the first modem. Its WiFi and LAN3 are its VLAN’s sub-vendo.',
         'A second modem: free LAN2 on the first one (untick it in WAN › Binding Options and in LAN › Layer 2/3 Port), then plug the second modem’s LAN1 into it.',
         'Without that change, chain them: the second modem’s LAN1 into the first one’s LAN4, and the internet line into the second modem’s LAN4.',
      ],
      Diagram: ModemDiagram,
   },
   {
      key: 'plain',
      tab: 'Setup C · Plain switch + tag-aware APs',
      title: 'Plain switch + access points that read VLAN tags — works, with a catch',
      steps: [
         'Internet line, mini PC and the access points into any switch; each access point tags its own VLAN.',
         'Anything untagged on that switch gets raw internet from the modem, so keep only tag-aware gear on it.',
      ],
      Diagram: PlainDiagram,
   },
];

/**
 * `intro` puts one more tab FIRST (the wizard uses it for the flashing steps),
 * so the whole guide is one tab strip instead of a wall of text — Ray,
 * 2026-09-23: "lack of space".
 */
const WiringSetups: React.FC<{ compact?: boolean; intro?: { tab: string; content: React.ReactNode } }> = ({
   compact,
   intro,
}) => {
   const [active, setActive] = useState<Setup['key'] | 'intro'>(intro ? 'intro' : 'managed');
   const setup = WIRING_SETUPS.find((s) => s.key === active) ?? WIRING_SETUPS[0];
   return (
      <Wrap data-testid="wiring-setups" $compact={!!compact}>
         <Tabs role="tablist" aria-label="Setup variations">
            {intro && (
               <Tab
                  type="button"
                  role="tab"
                  aria-selected={active === 'intro'}
                  $on={active === 'intro'}
                  onClick={() => setActive('intro')}
               >
                  {intro.tab}
               </Tab>
            )}
            {WIRING_SETUPS.map((s) => (
               <Tab
                  key={s.key}
                  type="button"
                  role="tab"
                  aria-selected={s.key === active}
                  $on={s.key === active}
                  onClick={() => setActive(s.key)}
               >
                  {s.tab}
               </Tab>
            ))}
         </Tabs>
         {intro && active === 'intro' ? (
            <PanelBox role="tabpanel" aria-label={intro.tab}>
               {intro.content}
            </PanelBox>
         ) : (
            <PanelBox role="tabpanel" aria-label={setup.tab}>
               <setup.Diagram />
            </PanelBox>
         )}
      </Wrap>
   );
};

export default WiringSetups;

const Wrap = styled.div<{ $compact: boolean }>`
   display: flex;
   flex-direction: column;
   gap: 10px;
   font-size: ${(p) => (p.$compact ? '12.5px' : '13.5px')};
   color: #374151;
   ol {
      margin: 6px 0 0 18px;
      padding: 0;
   }
   li {
      margin: 3px 0;
   }
`;
const Lead = styled.p`
   margin: 0;
   line-height: 1.5;
`;
const Tabs = styled.div`
   display: flex;
   gap: 6px;
   flex-wrap: wrap;
`;
const Tab = styled.button<{ $on: boolean }>`
   border: 1px solid ${(p) => (p.$on ? '#2563eb' : '#e5e7eb')};
   background: ${(p) => (p.$on ? '#eff6ff' : '#fff')};
   color: ${(p) => (p.$on ? '#1d4ed8' : '#374151')};
   font-weight: 600;
   font-size: 12.5px;
   padding: 6px 12px;
   border-radius: 999px;
   cursor: pointer;
`;
const PanelBox = styled.div`
   border: 1px solid #e5e7eb;
   border-radius: 10px;
   padding: 10px 12px;
   background: #fafafa;
   display: flex;
   flex-direction: column;
   gap: 8px;
`;
const PanelTitle = styled.div`
   margin-top: 8px;
   font-weight: 600;
   color: #111827;
`;
const Svg = styled.svg`
   width: 100%;
   height: auto;
   max-height: 240px;
   display: block;
   background: #fff;
   border-radius: 8px;
   border: 1px solid #eef2f7;
`;
